import { ApiError, apiCall, apiCallWithCsrfRefresh } from "./api";
import { dateToYmd } from "./dates";
import type { RoleOutcome } from "./journeyApi";

/**
 * SPA client for the v3 events API (`/api/events` — `src/modules/events/v3Routes.ts`).
 *
 * `GET /` returns the caller's saved events for EITHER principal — anon sessions
 * own real `CustomerEvent` rows too (customer-event-anon-ownable), so a signed-out
 * visitor gets their own events, not `[]`.
 * `GET /search?q=` returns catalog `Event` rows (anon-capable autocomplete).
 * `GET /search?from=YYYY-MM-DD` returns upcoming catalog rows from that date (no `q`).
 * `POST /` accepts either `{ eventCatalogId }` (link catalog row) or a custom-event
 * body — anon-capable for both (the row is scoped to the anon session and
 * re-points onto the customer on the login merge). `DELETE`/`PATCH`/role remain
 * customer-only (anon mutation of an existing event is out of scope).
 */

/** Distance categories the Add Event modal offers — mirrors the server enum. */
export type EventDistance =
  | "5k"
  | "10k"
  | "half"
  | "marathon"
  | "ultra"
  | "triathlon"
  | "hyrox"
  | "other";

/** A saved customer event DTO (`GET /api/events` — the customer's own events). */
export type EventDTO = {
  id: string;
  name: string;
  eventDate: string;
  sport: string | null;
  distanceMeters: number | null;
  distance: EventDistance | null;
  /** The free-form distance the customer chose (e.g. "12K"); null when none. */
  distanceLabel: string | null;
  type: string;
  location: string | null;
  locationCity: string | null;
  locationRegion: string | null;
  locationCountry: string | null;
  notes: string | null;
  /** Journeys-overhaul contract (P2, J4): goal = anchors its own journey; milestone =
   *  tune-up inside another journey's window (own race-day kit, no journey). Absent ⇒
   *  "goal" (server default). */
  intent?: "goal" | "milestone";
  /**
   * F1 (role-conflict robustness §6.1): the catalog `Event` row this saved
   * event was linked from, or null for a CUSTOM event. Date edits are custom-
   * only — catalog dates are shared rows, so the "Change date" affordances gate
   * on this being null/absent (absent = old server / custom; the PATCH itself
   * 422s `catalog_event_date_immutable` as the trust boundary).
   */
  eventCatalogId?: string | null;
};

/** "City, ST" line for forms — prefers city/region, falls back to catalog `location`. */
export function formatEventLocation(row: {
  location?: string | null;
  locationCity?: string | null;
  locationRegion?: string | null;
}): string {
  const fromParts = [row.locationCity, row.locationRegion].filter(Boolean).join(", ");
  return fromParts.trim() || row.location?.trim() || "";
}

/** One distance option a catalog race offers. `meters` is server-authoritative. */
export type CatalogDistanceOption = { label: string; meters: number | null };

/**
 * A catalog race returned by `GET /api/events/search`. Carries a start/end date
 * (multi-day weekends set `endDate`) and the free-form distance options the SPA
 * presents for the customer to pick from.
 */
export type CatalogEventDTO = {
  id: string;
  name: string;
  startDate: string;
  endDate: string | null;
  sport: string | null;
  distances: CatalogDistanceOption[];
  location: string | null;
  locationCity: string | null;
  locationRegion: string | null;
  locationCountry: string | null;
};

/**
 * Link an existing catalog race (`POST /api/events` catalog branch). `distanceLabel`
 * names which catalog distance the customer picked; required when the race offers
 * more than one. Metres are resolved server-side from the catalog row.
 */
export type LinkCatalogEventInput = {
  eventCatalogId: string;
  distanceLabel?: string;
  /** Journeys-overhaul contract (P2, J4). Absent ⇒ "goal". */
  intent?: "goal" | "milestone";
  /** See {@link CreateCustomEventInput.fromReplay}. */
  fromReplay?: boolean;
};

/** Custom event body (`POST /api/events` custom branch). */
export type CreateCustomEventInput = {
  name: string;
  eventDate: string;
  distance?: EventDistance;
  locationCity?: string;
  locationRegion?: string;
  locationCountry?: "US" | "CA" | "OTHER";
  notes?: string;
  /** Journeys-overhaul contract (P2, J4). Absent ⇒ "goal". */
  intent?: "goal" | "milestone";
  /**
   * Set ONLY by the anon→login race replay (`anonRaceReplay.ts`). It tells the
   * server "this intent came from a stored draft, not from a person answering
   * just now": a dedupe HIT then keeps the existing row's intent instead of
   * re-stamping it, so a stale guest draft can't downgrade a goal event the
   * athlete has since built a journey on. New events are still created with the
   * draft's intent. The interactive add-event funnel must NOT set it — its
   * eager-create-then-upgrade depends on the re-stamp.
   */
  fromReplay?: boolean;
};

export type CreateEventInput = LinkCatalogEventInput | CreateCustomEventInput;

export type CreateEventOutcome = { event: EventDTO; deduped: boolean };

export function isLinkCatalogEventInput(
  input: CreateEventInput
): input is LinkCatalogEventInput {
  return (
    typeof input === "object" &&
    input !== null &&
    "eventCatalogId" in input &&
    typeof (input as LinkCatalogEventInput).eventCatalogId === "string"
  );
}

export async function searchCatalogEvents(
  q: string,
  opts?: { limit?: number; signal?: AbortSignal }
): Promise<CatalogEventDTO[]> {
  const trimmed = q.trim();
  if (trimmed.length < 2) return [];
  const limit = opts?.limit ?? 10;
  const params = new URLSearchParams({ q: trimmed, limit: String(limit) });
  const res = await apiCall<{ events: CatalogEventDTO[] }>(`/api/events/search?${params}`, {
    signal: opts?.signal
  });
  return res.events;
}

/** Upcoming catalog rows from a calendar day — `GET /api/events/search?from=`. */
export async function browseCatalogEvents(
  fromYmd: string,
  opts?: { limit?: number; signal?: AbortSignal }
): Promise<CatalogEventDTO[]> {
  const trimmed = fromYmd.trim();
  if (!trimmed) return [];
  const limit = opts?.limit ?? 10;
  const params = new URLSearchParams({ from: trimmed, limit: String(limit) });
  const res = await apiCall<{ events: CatalogEventDTO[] }>(`/api/events/search?${params}`, {
    signal: opts?.signal
  });
  return res.events;
}

/** Calendar column `YYYY-MM-DD` from a catalog `startDate` ISO — matches `DateField` / server UTC semantics. */
export function catalogEventStartToYmd(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? dateToYmd(d) : "";
}

/**
 * The label to send when linking, or undefined when the user must still choose:
 *  - 0 options → undefined (no distance picker)
 *  - 1 option  → that label (auto-resolved)
 *  - >1        → undefined (caller must prompt for a choice)
 */
export function autoDistanceLabel(distances: CatalogDistanceOption[]): string | undefined {
  return distances.length === 1 ? distances[0].label : undefined;
}

/** Map a free-form catalog distance label to the nearest fixed `EventDistance` (for the custom/anon draft path). */
export function labelToEventDistance(label: string | null | undefined): EventDistance {
  switch ((label ?? "").trim().toLowerCase()) {
    case "marathon":
      return "marathon";
    case "half marathon":
    case "half":
      return "half";
    case "10k":
      return "10k";
    case "5k":
      return "5k";
    case "ultra":
      return "ultra";
    case "triathlon":
      return "triathlon";
    case "hyrox":
      return "hyrox";
    default:
      return "other";
  }
}

/** Human label for a fixed `EventDistance` id (mirrors the Add-event picker). */
export function eventDistanceLabel(distance: EventDistance): string {
  switch (distance) {
    case "5k":
      return "5K";
    case "10k":
      return "10K";
    case "half":
      return "Half marathon";
    case "marathon":
      return "Marathon";
    case "ultra":
      return "Ultra";
    case "triathlon":
      return "Triathlon";
    case "hyrox":
      return "Hyrox";
    default:
      return "Other";
  }
}

export async function loadEvents(signal?: AbortSignal): Promise<EventDTO[]> {
  const res = await apiCall<{ events: EventDTO[] }>("/api/events", { signal });
  return res.events;
}

export async function createEvent(
  input: CreateEventInput,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<CreateEventOutcome> {
  return apiCallWithCsrfRefresh<CreateEventOutcome>(
    "/api/events",
    { method: "POST", body: input, csrfToken },
    refreshCsrf
  );
}

/**
 * How an event delete should treat the active race journey that references it
 * (journeys-overhaul P2, J7): `archive` → the journey archives with the event;
 * `keep` → the journey stays active, keeping its stored race day. Sent as a
 * query param; the server ignores it until the P2 server lane lands (today's
 * behavior — silent SetNull — is unchanged), then starts requiring it when a
 * journey references the event.
 */
export type EventDeleteDisposition = "archive" | "keep";

/**
 * What to do with the nested tune-ups when deleting a GOAL that has milestones
 * inside its build window (role/conflict build-plan task S4/C4, spec §9.3, T5):
 * `keep_as_goal` (default) reverts each tune-up to a bare goal event + strips
 * its milestone kit; `remove` deletes them too. Sent alongside `disposition` as
 * a query param; the server (S4) requires it whenever the deleted goal's window
 * contains milestones (the `journey_attached` 409 carries the `tuneups` list).
 */
export type TuneupDisposition = "keep_as_goal" | "remove";

/**
 * What to do with the PlannedWorkouts linked to the deleted event(s) via
 * `customerEventId` (the applied-training-plan link): `keep` (default,
 * omitted) leaves them on the calendar unlinked — the old behaviour;
 * `remove` deletes them in the same server transaction as the event.
 */
export type WorkoutsDisposition = "keep" | "remove";

export async function deleteEvent(
  id: string,
  csrfToken: string,
  refreshCsrf: () => Promise<string>,
  opts?: {
    disposition?: EventDeleteDisposition;
    tuneupDisposition?: TuneupDisposition;
    workouts?: WorkoutsDisposition;
  }
): Promise<void> {
  const params = new URLSearchParams();
  if (opts?.disposition) params.set("disposition", opts.disposition);
  if (opts?.tuneupDisposition) params.set("tuneupDisposition", opts.tuneupDisposition);
  if (opts?.workouts) params.set("workouts", opts.workouts);
  const qs = params.toString() ? `?${params.toString()}` : "";
  await apiCallWithCsrfRefresh<void>(
    `/api/events/${encodeURIComponent(id)}${qs}`,
    { method: "DELETE", csrfToken },
    refreshCsrf
  );
}

/**
 * First-class event role change (spec §9.4, T7; build-plan S2/C2) — the ONLY
 * sanctioned way to flip `intent` after create. `POST /api/events/:id/role`,
 * customer-only, CSRF-guarded. On success the server returns `{ intent }`.
 *
 * The route can also reject with a structured 409/422 the caller must surface:
 *   - 422 `no_containing_build`         — goal→milestone with no build to nest into
 *   - 409 `ambiguous_build` `candidates`— >1 containing build; resend `intoJourneyId`
 *   - 409 `journey_overlap` `{ needsCollisionResolution, conflicts }` — milestone→goal
 *     that collides; the caller opens `RoleResolutionSheet` as a `pair`
 *     (parse with `needsCollisionResolutionFromError`).
 * Those bodies propagate as the thrown `ApiError` (see `journeyApi` parsers);
 * this fn does not swallow them.
 */
/** One resolved overlap riding a date-edit retry (build-plan §0.4/F1). Each
 *  entry names the journey it resolves so a stale pick can never archive the
 *  wrong build. `nest_existing` travels as the wire mode `"demote"`. */
export type EventDateOverlapResolution = {
  journeyId: string;
  mode: "chain" | "split" | "demote";
};

export type PatchEventDateBody = {
  /** The new race day, `YYYY-MM-DD`. */
  eventDate: string;
  /** Accumulated answers from prior `event_date_collision` 409 rounds. */
  overlapResolutions?: EventDateOverlapResolution[];
};

/**
 * Event date edit — `PATCH /api/events/:id` (spec §6.1, build-plan C1/F1).
 * CUSTOM events only; the server runs the same collision geometry as create:
 *   - 200 `{ event }`                      — updated (or same-date no-op)
 *   - 422 `catalog_event_date_immutable`   — catalog-linked date (shared row)
 *   - 400 `invalid_event_date`             — malformed / impossible YMD
 *   - 422 `milestone_outside_build`        — tune-up moved out of its build
 *   - 409 `event_date_collision`           — parse with
 *     `eventDateCollisionFromError`, answer via `overlapResolutions`, retry.
 * Errors propagate as the thrown `ApiError`; this fn does not swallow them.
 */
export async function patchEventDate(
  eventId: string,
  body: PatchEventDateBody,
  csrfToken: string,
  refreshCsrf: () => Promise<string>
): Promise<{ event: EventDTO }> {
  return apiCallWithCsrfRefresh<{ event: EventDTO }>(
    `/api/events/${encodeURIComponent(eventId)}`,
    { method: "PATCH", body, csrfToken },
    refreshCsrf
  );
}

/**
 * 409 body when a date edit moves the event into another build's window
 * (build-plan C1) — deliberately the SAME field shapes as `journey_overlap`
 * so the `RoleResolutionSheet` pair flow is reused verbatim. `conflictCount`
 * is the number of journeys STILL colliding at this 409 (§6.3); the client
 * loops sheet → accumulate → retry until it reaches zero.
 */
export type EventDateCollision = {
  error: "event_date_collision";
  existing: {
    journeyId: string;
    raceName: string | null;
    raceYmd: string | null;
    startYmd: string | null;
    /** Defaults true — it 409'd on overlap. */
    hasJourney?: boolean;
    /** §6.1 stitch (ADDITIVE — absent on an old server): the EVENT anchoring
     *  the conflicting journey (null for anon/event-less builds) + its
     *  catalog-ness (null ⇒ CUSTOM ⇒ date-editable). Mirrors
     *  `journey_overlap.existing`. */
    customerEventId?: string | null;
    eventCatalogId?: string | null;
  };
  /** The moved event's context (name + the attempted new ymd). */
  newEvent?: { id?: string | null; name?: string | null; ymd?: string | null } | null;
  resolutions: RoleOutcome[];
  conflictCount?: number;
  /** G5 consequence preview — parsed tolerantly; absent on an old server. */
  consequences?: {
    demote?: { removedSessions: number };
    split?: { removedSessions: number };
    chain?: { newStartYmd: string };
  } | null;
};

/** Coerce an arbitrary array to valid `RoleOutcome`s, dropping unknowns.
 *  Mirrors journeyApi's parser (kept local — that file is Lane B's): the
 *  legacy wire mode `"demote"` normalizes to `nest_existing`. */
function parseEventDateOutcomes(raw: unknown): RoleOutcome[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(
      (r): r is RoleOutcome | "demote" =>
        r === "nest_new" || r === "nest_existing" || r === "chain" || r === "demote"
    )
    // Legacy wire mode `"demote"` → `nest_existing`. `split` is no longer a pair
    // outcome (dropped by the filter above).
    .map((r): RoleOutcome => (r === "demote" ? "nest_existing" : r));
}

/**
 * Extract the structured `event_date_collision` payload from a failed date
 * PATCH, or null for any other error (the idiom of journeyApi's
 * `journeyOverlapConflictFromError` — implemented here because the 409 is an
 * events-API contract). Tolerant: unknown resolution names are dropped and the
 * additive §6.3/G5 fields may be absent.
 */
export function eventDateCollisionFromError(err: unknown): EventDateCollision | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as
    | (Partial<EventDateCollision> & { resolutions?: unknown; consequences?: unknown })
    | null;
  if (!body || typeof body !== "object" || body.error !== "event_date_collision") return null;
  if (!body.existing || typeof body.existing !== "object") return null;
  const resolutions = parseEventDateOutcomes(body.resolutions);
  if (resolutions.length === 0) return null;
  const ex = body.existing;
  const ne = body.newEvent ?? null;
  return {
    error: "event_date_collision",
    existing: {
      journeyId: String(ex.journeyId ?? ""),
      raceName: ex.raceName ?? null,
      raceYmd: ex.raceYmd ?? null,
      startYmd: ex.startYmd ?? null,
      hasJourney: ex.hasJourney ?? true,
      // §6.1 additive identity fields — tolerate absence (old server) or junk.
      customerEventId:
        typeof ex.customerEventId === "string" && ex.customerEventId
          ? ex.customerEventId
          : null,
      eventCatalogId:
        typeof ex.eventCatalogId === "string" && ex.eventCatalogId
          ? ex.eventCatalogId
          : null
    },
    newEvent: ne ? { id: ne.id ?? null, name: ne.name ?? null, ymd: ne.ymd ?? null } : null,
    resolutions,
    conflictCount:
      typeof body.conflictCount === "number" && Number.isFinite(body.conflictCount)
        ? body.conflictCount
        : undefined,
    consequences: parseEventDateConsequences(body.consequences)
  };
}

/** Loose passthrough of the G5 `consequences` block — keep only known,
 *  well-typed keys so a malformed payload can't leak into the UI. */
function parseEventDateConsequences(raw: unknown): EventDateCollision["consequences"] {
  if (!raw || typeof raw !== "object") return undefined;
  const c = raw as {
    demote?: { removedSessions?: unknown } | null;
    split?: { removedSessions?: unknown } | null;
    chain?: { newStartYmd?: unknown } | null;
  };
  const out: NonNullable<EventDateCollision["consequences"]> = {};
  if (c.demote && typeof c.demote.removedSessions === "number") {
    out.demote = { removedSessions: c.demote.removedSessions };
  }
  if (c.split && typeof c.split.removedSessions === "number") {
    out.split = { removedSessions: c.split.removedSessions };
  }
  if (c.chain && typeof c.chain.newStartYmd === "string") {
    out.chain = { newStartYmd: c.chain.newStartYmd };
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

export async function reclassifyEvent(
  id: string,
  to: "goal" | "milestone",
  csrfToken: string,
  refreshCsrf: () => Promise<string>,
  opts?: { intoJourneyId?: string }
): Promise<{ intent: "goal" | "milestone" }> {
  return apiCallWithCsrfRefresh<{ intent: "goal" | "milestone" }>(
    `/api/events/${encodeURIComponent(id)}/role`,
    {
      method: "POST",
      body: { to, ...(opts?.intoJourneyId ? { intoJourneyId: opts.intoJourneyId } : {}) },
      csrfToken
    },
    refreshCsrf
  );
}
